/**
 * The debug view's reads (`architecture/0029`). Each is enabled only while the
 * switch is on, so an account that never turns it on never asks — and one
 * without the flag would get a 404 from every one of these if it did.
 */
import { useQuery } from "@tanstack/react-query";
import type { AnswerStep, DebugDocument, DebugImport, DebugRun, DebugThread } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

export const debugKeys = {
  document: (projectId: string, documentId: string) =>
    ["project", projectId, "debug", "document", documentId] as const,
  import: (projectId: string, importId: string) => ["project", projectId, "debug", "import", importId] as const,
  thread: (projectId: string, threadId: string) => ["project", projectId, "debug", "thread", threadId] as const,
  run: (projectId: string, runId: string) => ["project", projectId, "debug", "run", runId] as const,
};

export function useDebugDocument(projectId: string, documentId: string, enabled: boolean) {
  return useQuery({
    queryKey: debugKeys.document(projectId, documentId),
    enabled,
    queryFn: async (): Promise<DebugDocument> => {
      const res = await api.debug.document({ params: { id: projectId, docId: documentId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useDebugImport(projectId: string, importId: string, enabled: boolean) {
  return useQuery({
    queryKey: debugKeys.import(projectId, importId),
    enabled,
    queryFn: async (): Promise<DebugImport> => {
      const res = await api.debug.import({ params: { id: projectId, importId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useDebugThread(projectId: string, threadId: string, enabled: boolean) {
  return useQuery({
    queryKey: debugKeys.thread(projectId, threadId),
    enabled,
    queryFn: async (): Promise<DebugThread> => {
      const res = await api.debug.thread({ params: { id: projectId, threadId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/**
 * The answer's steps (Phase 5, 2026-09-26), read through the tenant's own
 * `listSteps`, which any member of the project may read: the debug view puts
 * the model's turns between them. Empty for an answer of the old path and a
 * scripted demo answer.
 */
export function useDebugSteps(projectId: string, threadId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...debugKeys.thread(projectId, threadId), "steps"],
    enabled,
    queryFn: async (): Promise<AnswerStep[]> => {
      const res = await api.chat.listSteps({ params: { id: projectId, threadId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.steps;
    },
  });
}

/** One model call, whole. Fetched when a person opens it: bodies can be large. */
export function useDebugRun(projectId: string, runId: string, enabled: boolean) {
  return useQuery({
    queryKey: debugKeys.run(projectId, runId),
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<DebugRun> => {
      const res = await api.debug.run({ params: { id: projectId, runId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.run;
    },
  });
}
